// Zugriff auf die API der Static Web App. Anmeldung übernimmt Azure (Cookie); bei 401 geht es zur Microsoft-Anmeldung.
import { abfrage as aktivitaetAbfrage, type AktivitaetAntwort, type AktivitaetFilter } from './aktivitaet';
import { dateinameAus, type ExportBereich } from './listenexport';

export class ApiFehler extends Error {
  constructor(public status: number, message: string, public daten?: unknown) {
    super(message);
  }
}

/** Seite nach dem Abmelden: lädt keine Daten und leitet nicht von selbst wieder zur Anmeldung. */
export const ABGEMELDET = '/abgemeldet';
export const anmeldenUrl = (ziel = location.pathname + location.search + location.hash) =>
  `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(ziel === ABGEMELDET ? '/' : ziel)}`;
export const abmeldenUrl = () => `/.auth/logout?post_logout_redirect_uri=${ABGEMELDET}`;
/** Beendet die Microsoft-Sitzung im Browser – danach fragt die Anmeldung wieder nach dem Konto. */
export const microsoftAbmeldenUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout';

/** Sitzungszustand: aktiv, sobald die App einmal geladen ist; abgelaufen() setzt die Oberfläche. */
export const sitzung = { aktiv: false, abgelaufen: (_grund: string) => {} };

async function anfrage<T>(methode: string, pfad: string, body?: unknown, nochmal = true): Promise<T> {
  // FormData (Datei-Upload) geht unverändert als multipart/form-data hinaus, alles andere als JSON
  const form = body instanceof FormData;
  const res = await fetch('/api/' + pfad, {
    method: methode,
    headers: body === undefined || form ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : form ? body : JSON.stringify(body),
    credentials: 'same-origin',
  });
  if (res.status === 401) {
    const meldung = (await res.json().catch(() => null))?.meldung || 'Bitte anmelden.';
    // Läuft die App schon, nie selbst umleiten (das sah aus wie ein Neuladen und verwarf ungespeicherte Eingaben):
    // die Oberfläche zeigt „Anmeldung abgelaufen“ mit Knopf zum Neu-Anmelden.
    if (sitzung.aktiv) {
      // Static Web Apps liefert gelegentlich einmalig einen Prinzipal ohne E-Mail-Adresse – kurz warten, einmal wiederholen
      // (unbedenklich auch bei Änderungen: eine 401 heißt, die API hat nichts ausgeführt)
      if (nochmal) {
        await new Promise((r) => setTimeout(r, 1500));
        return anfrage<T>(methode, pfad, body, false);
      }
      sitzung.abgelaufen(`${methode} /api/${pfad}: ${meldung}`);
      throw new ApiFehler(401, `Anmeldung abgelaufen (${meldung})`);
    }
    // Erster Aufruf: zur Anmeldung, aber nur einmal je Minute – lehnt die API auch direkt danach ab, entstünde sonst eine Schleife
    let zuletzt = 0;
    try { zuletzt = Number(sessionStorage.getItem('pa-anmeldung') || 0); } catch { /* ohne Speicher: weiterleiten */ }
    if (Date.now() - zuletzt > 60_000) {
      try { sessionStorage.setItem('pa-anmeldung', String(Date.now())); } catch { /* egal */ }
      location.href = anmeldenUrl();
    }
    throw new ApiFehler(401, `Anmeldung abgelehnt (${meldung})`);
  }
  const text = await res.text();
  const daten = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiFehler(res.status, daten?.meldung || `Fehler ${res.status}`, daten);
  return daten as T;
}

export type Rolle = 'admin' | 'verantwortlich' | 'mitwirkend' | 'lesend';

export interface ProjektKurz {
  id: string; titel: string; untertitel?: string; version?: string; stand?: string; rolle: Rolle;
  /** Archiviert (nur lesbar) seit; die Startseite blendet solche Projekte standardmäßig aus. */
  archiviertAm?: string | null; loeschenAm?: string | null;
  /** Von mir als Favorit markiert (Stern) – persönlich, für niemanden sonst sichtbar */
  favorit?: boolean;
}
/** Archivstand eines Projekts: archiviert am/von, wird am loeschenAm endgültig gelöscht (Aufbewahrungsfrist in Monaten). */
export interface Archiv { am: string; von: string | null; loeschenAm: string; monate: number }
export interface Kunde { id: string; name: string; logo?: string | null; logoRatio?: number | null; intern?: boolean; /** von mir als Favorit markiert (Stern) */ favorit?: boolean; projekte: ProjektKurz[] }
export interface Ich { email: string; name: string; superadmin: boolean; kunden: Kunde[] }

/**
 * Ticketsystem-Einstellungen eines Projekts. eingerichtet: Server, Schlüssel und Token sind da (Superadmin);
 * kto/kundeName: checkITeasy-Kunde der Kundeninstanz; bereit: eingerichtet, Kunde zugeordnet und eingeschaltet.
 */
export interface TicketEinstellungen {
  eingerichtet: boolean; kto: string | null; kundeName: string | null; aktiv: boolean; bereit: boolean;
  ciProjektId: number | null; ciProjektName: string | null; queueId: number | null; queueName: string | null;
  kategorieId: number | null; kategorieName: string | null; letzterSync: string | null; syncFehler: string | null;
}
export interface TicketAuswahl { id: number; name: string }
export interface TicketAuswahllisten {
  projekte: TicketAuswahl[]; queues: TicketAuswahl[]; kategorien: TicketAuswahl[];
  standardQueueId: number | null; standardKategorieId: number | null;
}
/**
 * Nach dem Hinzufügen eines Mitglieds von Kundenseite (nur wenn dem Kunden ein checkITeasy-Kunde zugeordnet ist):
 * Der Ansprechpartner war in checkITeasy schon vorhanden, wurde angelegt, oder es gab einen Fehler (nur Hinweis).
 */
export interface TicketAnsprechpartnerStand { stand: 'vorhanden' | 'angelegt' | 'fehler'; meldung?: string }
/** Kundeninstanz mit ihrer Kundennummer in checkITeasy – jedes Ticket ihrer Projekte geht an diesen Kunden. */
export interface TicketKundeZuordnung { id: string; name: string; ticketKto: string | null; ticketKundeName: string | null }
/** Kunde aus checkITeasy (Auswahl an der Kundeninstanz). */
export interface TicketKunde { kto: string; name: string }
/**
 * Einrichtung des Ticketsystems checkITeasy (nur Superadmins). Das Token selbst kommt nie zurück, nur „hinterlegt“.
 * serverEingerichtet: App-Einstellungen CHECKITEASY_URL/CHECKITEASY_MANDANT; schluesselEingerichtet: GEHEIMNIS_SCHLUESSEL.
 */
export interface TicketsystemStand {
  eingerichtet: boolean; serverEingerichtet: boolean; schluesselEingerichtet: boolean; tokenGesetzt: boolean;
  geaendertVon: string | null; geaendertAm: string | null; kunden: TicketKundeZuordnung[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Daten = any;
export type Zeilen = Record<string, Record<string, string> | string>;
export interface ProjektAntwort { daten: Daten; zeilen: Zeilen; nr: number; rolle: Rolle; archiv?: Archiv | null }
export interface Aenderung { sammlung: string; id: string; op: 'put' | 'del'; element?: Daten; zeile?: string | null }
export interface SpeicherAntwort { nr: number; zeilen: Zeilen; version: string | null }
export interface SeitAntwort {
  nr: number;
  elemente?: Record<string, Daten[]>;
  geloescht?: Record<string, string[]>;
  zeilen?: Zeilen;
  meta?: Daten;
  history?: Daten[];
}

export interface KundeDaten { id: string; name: string; logo?: string | null; logoRatio?: number | null; intern?: boolean }
/** domaene: E-Mail-Domäne (null bei allgemeinen Mail-Anbietern); firma: dazu eingetragener Firmenname (null = noch keiner) */
export interface Mitglied { email: string; name?: string | null; rolle: Rolle; eingeladenVon?: string | null; am: string; zuletzt?: string | null; domaene?: string | null; firma?: string | null }
export interface Person { email: string; name?: string | null; firma?: string | null }
/** Eine im Projekt eingetragene Person (E-Mail) oder ein freier Name bzw. Kürzel mit der Zahl der Einträge je Bereich. */
export interface Zustaendigkeit { person: string; name?: string | null; mitglied: boolean; anzahl: number; offen: number; bereiche: Record<string, number> }
export interface Superadmin { email: string; name?: string | null; ernanntVon?: string | null; am?: string | null; fest: boolean }
export interface Benutzer { email: string; name?: string | null; zuletzt?: string | null; superadmin: boolean; fest: boolean; projekte: { id: string; titel: string; kunde: string; rolle: Rolle }[] }
/** Ergebnis des Einladungsversands; null = keine Einladung nötig (z. B. nur Rolle geändert). */
export type Einladung = 'gesendet' | 'nicht-eingerichtet' | 'fehler' | null;
export type BezugTyp = 'schritt' | 'prozess' | 'entscheidung' | 'projekt' | 'aufgabe' | 'offenerPunkt';
export interface Kommentar {
  id: string; bezugTyp: BezugTyp; bezugId: string; text: string; von: string; vonName?: string | null; am: string;
  /** Der Kommentar stammt aus einer Ticketnotiz in checkITeasy */
  ausTicket?: boolean;
  /** Automatische Notiz: „termin“ = Beginn oder Fälligkeit der Aufgabe verschoben bzw. entfernt (nicht änderbar) */
  art?: 'termin';
}
export interface Sicherung { am: string; nr?: number | null; angelegtVon?: string | null; angelegtAm: string; groesse: number }
// Aufgaben (wie Asana). Rechte: lesen ab lesend; Aufgaben anlegen/ändern ab mitwirkend; löschen eigene ab mitwirkend,
// fremde ab verantwortlich; Abschnitte pflegen ab verantwortlich. „zeile“ = Zeilenversion für 409/Zusammenführung.
export interface Abschnitt { id: string; name: string; reihenfolge: number; zeile: string }
export type AufgabeBezugTyp = 'schritt' | 'prozess' | 'entscheidung';
export type Prioritaet = 'niedrig' | 'mittel' | 'hoch';
export interface Aufgabe {
  /** Sichtbare ID je Projekt (fortlaufend, vom Server vergeben, nicht änderbar) – Anzeige über nrText */
  nr: number;
  id: string; projektId: string; abschnittId: string | null; elternId: string | null;
  titel: string; beschreibung: string | null;
  /** E-Mail eines Mitglieds (klein geschrieben) oder freier Name */
  zustaendig: string | null;
  /** Weitere Beteiligte neben der verantwortlichen Person (zustaendig) (E-Mails von Projektmitgliedern, sortiert) */
  beteiligte?: string[];
  /** JJJJ-MM-TT; Start im Gantt-Plan (Ende = faellig) */
  beginn: string | null;
  /** JJJJ-MM-TT */
  faellig: string | null;
  /** Aufgaben, die vorher fertig sein müssen (Ende → Anfang) */
  vorgaenger: string[];
  erledigt: boolean; erledigtAm: string | null; prioritaet: Prioritaet | null; reihenfolge: number;
  bezugTyp: AufgabeBezugTyp | null; bezugId: string | null;
  erstelltVon: string | null; erstelltAm: string; geaendertVon: string | null; geaendertAm: string | null;
  zeile: string;
  /** Von mir gemerkt (Stern) – persönlich, gehört nicht zur Zeile der Aufgabe */
  gemerkt?: boolean;
  /** Ticket in checkITeasy zu dieser Aufgabe (nur Anzeige), sonst null */
  ticket?: AufgabeTicket | null;
}
/**
 * Ticket in checkITeasy an einer Aufgabe. Alles kommt aus dem Ticketsystem und ist hier nicht änderbar.
 * naechsterTermin: Ortszeit des Ticketsystems ohne Zeitzone (JJJJ-MM-TTThh:mm:ss); Termine der checkITeasy-KI fehlen.
 * sendefehler: Änderungen der Aufgabe ließen sich auch nach mehreren Versuchen nicht ins Ticket senden.
 */
export interface AufgabeTicket {
  ticketId: number; nummer: string | null; link: string | null;
  /** Das Ticket gab es schon und wurde nur verknüpft: Titel, Beschreibung und Personen des Tickets bleiben, wie sie in checkITeasy stehen */
  verknuepft?: boolean;
  abgeschlossen: boolean; abgeschlossenAm: string | null; status: string | null;
  aufwand: number | null; fortschritt: number | null; fortschrittProzent: number | null; restOffen: number | null;
  freigabeOffen: boolean; naechsterTermin: string | null; naechsterTerminBetreff: string | null; sendefehler: string | null;
}
/** Offenes Ticket des Kunden in der Auswahl „Mit Ticket verknüpfen“; gleichesProjekt = gehört zum checkITeasy-Projekt dieses Projekts. */
export interface TicketWahl {
  ticketId: number; nummer: string | null; titel: string; auszug: string; status: string | null; faellig: string | null;
  projekt: string | null; gleichesProjekt: boolean;
  /** Ganze Beschreibung als Klartext – nur für den Filter im Dialog. */
  text?: string | null;
}
/** Für die Aufgabenansicht: bereit = im Projekt lassen sich Tickets erzeugen und abgleichen; syncFehler sehen nur Admins. */
/** Ergebnis eines Abgleichs mit checkITeasy; gelaufen = false: nicht fällig (höchstens alle fünf Minuten) oder nicht eingerichtet. */
export interface TicketAbgleich { gelaufen: boolean; tickets: number; kommentare: number; letzterSync: string | null; fehler: string | null }
export interface TicketProjektStand { bereit: boolean; aktiv: boolean; letzterSync: string | null; syncFehler: string | null }
/** Eintrag in „Meine Aufgaben“: zusätzlich Projekt und Kunde. */
export interface MeineAufgabe extends Aufgabe { kundeId: string; projektTitel: string; kundeName: string }
/** Änderbare Felder; nur mitgeschickte werden geändert, null leert ein Feld. bezugTyp und bezugId gehören zusammen. */
export interface AufgabeFelder {
  titel?: string; beschreibung?: string | null; zustaendig?: string | null; beginn?: string | null; faellig?: string | null;
  vorgaenger?: string[]; beteiligte?: string[];
  erledigt?: boolean; prioritaet?: Prioritaet | null; abschnittId?: string | null; reihenfolge?: number;
  elternId?: string | null; bezugTyp?: AufgabeBezugTyp | null; bezugId?: string | null;
}
export type NeueAufgabe = AufgabeFelder & { titel: string };
export type AufgabeFeld = 'titel' | 'beschreibung' | 'zustaendig' | 'beteiligte' | 'beginn' | 'faellig' | 'vorgaenger' | 'erledigt' | 'prioritaet' | 'abschnittId' | 'reihenfolge' | 'elternId' | 'bezug';
/** Antwort auf PATCH: zusammengefuehrt = inzwischen geändert, aber nur andere Felder (vom Server vereint). */
export interface AufgabeGeaendert { aufgabe: Aufgabe; zusammengefuehrt: boolean }
/** Inhalt von ApiFehler.daten bei 409: aktueller Stand und strittige Felder. */
export interface AufgabeKonflikt { meldung: string; aktuell: Aufgabe; konflikte: AufgabeFeld[] }
export interface AufgabeVerlauf { nr: number; von: string; vonName?: string | null; am: string; was: string; feld: AufgabeFeld | null; alt: string | null; neu: string | null }
export interface AufgabenFilter { bezugTyp?: AufgabeBezugTyp; bezugId?: string; erledigt?: boolean }

// Projektsteuerung: Meilensteine/Phasen (schreiben ab verantwortlich) und offene Punkte (schreiben ab mitwirkend,
// löschen eigene ab mitwirkend, fremde ab verantwortlich). „zeile“ = Zeilenversion; 409 → ApiFehler.daten = { meldung, aktuell }.
export type MeilensteinArt = 'phase' | 'meilenstein';
export type MeilensteinStatus = 'geplant' | 'laeuft' | 'erreicht' | 'verschoben';
export interface Meilenstein {
  /** Sichtbare ID je Projekt (fortlaufend, vom Server vergeben, nicht änderbar) – Anzeige über nrText */
  nr: number;
  id: string; projektId: string; art: MeilensteinArt; titel: string; beschreibung: string | null;
  /** JJJJ-MM-TT, nur bei Phasen */
  beginn: string | null;
  /** JJJJ-MM-TT: Ende der Phase bzw. Termin des Meilensteins */
  termin: string | null;
  status: MeilensteinStatus;
  /** E-Mail eines Mitglieds oder freier Name */
  verantwortlich: string | null;
  /** Weitere Beteiligte neben der verantwortlichen Person (E-Mails von Projektmitgliedern, sortiert) */
  beteiligte?: string[];
  reihenfolge: number;
  erstelltVon: string | null; erstelltAm: string; geaendertVon: string | null; geaendertAm: string | null;
  zeile: string;
}
export type MeilensteinFelder = Partial<Pick<Meilenstein, 'art' | 'titel' | 'beschreibung' | 'beginn' | 'termin' | 'status' | 'verantwortlich' | 'beteiligte' | 'reihenfolge'>>;
export type PunktArt = 'risiko' | 'annahme' | 'klaerung';
export type PunktStatus = 'offen' | 'in_klaerung' | 'erledigt';
export type Stufe = 'niedrig' | 'mittel' | 'hoch';
export interface OffenerPunkt {
  id: string; projektId: string; art: PunktArt; titel: string; beschreibung: string | null;
  verantwortlich: string | null;
  /** Weitere Beteiligte neben der verantwortlichen Person (E-Mails von Projektmitgliedern, sortiert) */
  beteiligte?: string[];
  /** JJJJ-MM-TT */
  termin: string | null;
  status: PunktStatus;
  /** nur bei Risiken */
  wahrscheinlichkeit: Stufe | null; auswirkung: Stufe | null;
  massnahme: string | null;
  erledigtAm: string | null;
  erstelltVon: string | null; erstelltAm: string; geaendertVon: string | null; geaendertAm: string | null;
  zeile: string;
}
export type PunktFelder = Partial<Pick<OffenerPunkt, 'art' | 'titel' | 'beschreibung' | 'verantwortlich' | 'beteiligte' | 'termin' | 'status' | 'wahrscheinlichkeit' | 'auswirkung' | 'massnahme'>>;

// Projekt-Cockpit (lesen alle Mitglieder): Kennzahlen in einer Anfrage; Listen sind auf 20 Einträge gekürzt.
export interface CockpitMeilenstein { id: string; art: MeilensteinArt; titel: string; beginn: string | null; termin: string | null; status: MeilensteinStatus; verantwortlich: string | null }
export interface CockpitDaten {
  heute: string; wochenbeginn: string;
  /** nachStatus: umgesetzt, in_arbeit, offen, freigabe_offen, nicht_angeboten, ohne (ohne Start und Ende) */
  schritte: { gesamt: number; nachStatus: Record<string, number> };
  entscheidungen: { offen: number; ueberfaellig: number; entschieden: number;
    liste: { id: string; frage: string; termin: string | null; verantwortlich: string | null; processId: string; nodeId: string | null; ueberfaellig: boolean }[] };
  aufgaben: { offen: number; ueberfaellig: number; erledigtWoche: number; gesamt: number;
    ueberfaelligListe: { id: string; titel: string; zustaendig: string | null; faellig: string | null }[] };
  meilensteine: { gesamt: number; erreicht: number; naechste: CockpitMeilenstein[]; ueberfaellig: CockpitMeilenstein[] };
  offenePunkte: { offen: number; nachArt: Record<PunktArt, number>; risikenNachStufe: Record<Stufe | 'ohne', number>;
    risiken: { id: string; titel: string; stufe: Stufe | null; status: PunktStatus; verantwortlich: string | null; termin: string | null; massnahme: string | null }[];
    ueberfaellig: { id: string; art: PunktArt; titel: string; termin: string | null; verantwortlich: string | null }[] };
  besprechungen: { id: string; titel: string; datum: string | null; anzahl: number }[];
  checklisten: { id: string; titel: string; gesamt: number; erledigt: number }[];
  tests: { gesamt: number; offen: number; bestanden: number; fehler: number; blockiert: number;
    probleme: { id: string; titel: string; status: TestStatus; prozessId: string; schrittId: string | null; tester: string | null; getestetAm: string | null }[] };
}

// Besprechungen: Protokoll des Skills gespraechsprotokoll importieren (Vorschau und Import ab verantwortlich).
export type ImportArt = 'aufgabe' | 'entscheidung' | 'offenerPunkt' | 'meilenstein' | 'ignorieren';
export interface ProtokollZeile {
  nr: number;
  /** entscheidung (Beschluss), frage, punkt (To-do), meilenstein, aufgabe */
  quelle: string;
  vorschlag: ImportArt;
  titel: string; beschreibung: string | null; thema: string | null;
  /** Firma bzw. Person aus der Datei (Hinweis für die Zuordnung) */
  wer: string | null;
  /** JJJJ-MM-TT; terminText = Originaltext (z. B. „[zu klären]“) */
  termin: string | null; terminText: string | null; status: string | null;
}
export interface ProtokollVorschau { titel: string; datum: string | null; teilnehmer: string[]; kunde: string | null; gespraechstyp: string | null; datei: string; zeilen: ProtokollZeile[] }
export interface ImportZeile { art: ImportArt; titel: string; beschreibung?: string | null; zustaendig?: string | null; beteiligte?: string[]; termin?: string | null; prozessId?: string; entschieden?: boolean }
export interface BesprechungImport { titel: string; datum: string | null; teilnehmer: string[]; datei: string | null; prozessId?: string; zeilen: ImportZeile[] }
export interface Besprechung {
  id: string; projektId: string; titel: string; datum: string | null; teilnehmer: string[]; datei: string | null;
  erstelltVon: string | null; erstelltAm: string; eintraege: { art: Exclude<ImportArt, 'ignorieren'>; id: string; titel: string }[];
}

// Dokumente/Anhänge. Rechte: lesen alle Mitglieder; hochladen ab mitwirkend; löschen eigene ab mitwirkend, fremde ab
// verantwortlich. Ohne eingerichtete Ablage (App-Einstellung ANHAENGE_SPEICHER) antworten alle Aufrufe mit 503.
export type AnhangBezugTyp = 'projekt' | 'prozess' | 'schritt' | 'entscheidung' | 'aufgabe';
export interface Anhang {
  id: string; bezugTyp: AnhangBezugTyp; bezugId: string; dateiname: string; groesse: number; inhaltstyp: string;
  hochgeladenVon: string; hochgeladenVonName: string | null; hochgeladenAm: string;
  /** Das Element, an dem die Datei hing, ist gelöscht (die Datei bleibt erhalten). */
  verwaist?: boolean;
  /** Weitere Stellen, an denen dieselbe Datei verknüpft ist (bezugTyp/bezugId oben = Ort des Hochladens). */
  verknuepfungen?: AnhangVerknuepfung[];
  /** Link statt Datei (SharePoint, Webseite …): dateiname ist der Titel, groesse 0 */
  url?: string;
}
export interface AnhangVerknuepfung {
  bezugTyp: AnhangBezugTyp; bezugId: string; verknuepftVon: string; verknuepftVonName: string | null; verknuepftAm: string; verwaist?: boolean;
}
/** Kurzlebiger Download-Link (SAS, 5 Minuten gültig). */
/** extern: Link (SharePoint, Webseite …) – url ist die Adresse selbst, in neuem Tab öffnen */
export interface AnhangLink { url: string; gueltigBis?: string; dateiname: string; extern?: boolean }

// Testszenarien je Prozessschritt; lesen alle, anlegen/ändern ab mitwirkend, löschen eigene ab mitwirkend, fremde ab verantwortlich.
export type TestStatus = 'offen' | 'bestanden' | 'fehler' | 'blockiert';
export interface Testfall {
  id: string; projektId: string; prozessId: string; schrittId: string | null; titel: string;
  /** Ablauf und erwartetes Ergebnis */
  beschreibung: string | null; testdaten: string | null; status: TestStatus;
  /** tatsächliches Ergebnis / Abweichung */
  ergebnis: string | null; tester: string | null; getestetAm: string | null; reihenfolge: number;
  /** Weitere Beteiligte neben dem Tester (E-Mails von Projektmitgliedern, sortiert) */
  beteiligte?: string[];
  erstelltVon: string | null; erstelltAm: string; geaendertVon: string | null; geaendertAm: string | null; zeile: string;
}
export type TestfallFelder = Partial<Pick<Testfall, 'titel' | 'beschreibung' | 'testdaten' | 'status' | 'ergebnis' | 'tester' | 'beteiligte' | 'getestetAm' | 'reihenfolge'>>;

// Go-Live-/Schulungs-Checklisten; lesen alle, Punkte pflegen/abhaken ab mitwirkend, Checklisten anlegen/löschen ab verantwortlich.
export interface ChecklistenPunkt {
  id: string; checklisteId: string; text: string;
  /** Verantwortlich: E-Mail eines Mitglieds oder freier Name (API-Feld „zustaendig“) */
  zustaendig: string | null; faellig: string | null;
  /** Weitere Beteiligte neben der verantwortlichen Person (E-Mails von Projektmitgliedern, sortiert) */
  beteiligte?: string[];
  erledigt: boolean; erledigtVon: string | null; erledigtAm: string | null; reihenfolge: number; erstelltVon: string | null; zeile: string;
}
export interface Checkliste { id: string; titel: string; vorlage: string | null; reihenfolge: number; erstelltVon: string | null; erstelltAm: string; zeile: string; punkte: ChecklistenPunkt[] }
export interface ChecklistenVorlage { id: string; titel: string; punkte: string[] }
export type ChecklistenPunktFelder = Partial<Pick<ChecklistenPunkt, 'text' | 'zustaendig' | 'beteiligte' | 'faellig' | 'erledigt' | 'reihenfolge'>>;

/** Treffer der Server-Suche; ziel = aufgabe | offenerPunkt | schritt | prozess | entscheidung | projekt | meilensteine | checklisten | tests | cockpit | dokumente */
export interface Suchtreffer { art: string; id: string; titel: string; ausschnitt: string | null; ziel: string; zielId: string | null }

// Abnahmen: freigegebene, eingefrorene Stände; lesen alle Mitglieder, freigeben ab verantwortlich.
export interface Abnahme {
  id: string; nr: number; version: string | null; aenderungsnr: number;
  /** null = ganzes Projekt */
  prozessId: string | null; prozessTitel: string | null; bemerkung: string | null;
  freigegebenVon: string; freigegebenVonName: string | null; freigegebenAm: string;
}
export interface Unterschied {
  art: 'angelegt' | 'gelöscht' | 'geändert'; sammlung: string; typ: string; id: string; titel: string; prozess: string | null;
  felder: { feld: string; name: string; alt: string; neu: string }[];
}
/** Stand: dauerhaft aufbewahrte Prozessdaten; nr = Änderungsnummer. herkunft: version | manuell | sicherung */
export interface Stand {
  nr: number; version: string | null; am: string; von: string | null; vonName: string | null; bezeichnung: string | null; herkunft: string;
}
/** links/rechts: Freigabe-ID, „stand:<nr>“ oder „aktuell“ */
export interface Vergleich { links: string; rechts: string; unterschiede: Unterschied[] }

export type NeuesProjekt = { kundeId: string; titel: string; id?: string } & ({ art: 'leer' } | { art: 'kopie' | 'vorlage'; kopieVon: string } | { art: 'json'; daten: Daten });

const p = encodeURIComponent;

export const api = {
  ich: () => anfrage<Ich>('GET', 'ich'),
  projekt: (id: string) => anfrage<ProjektAntwort>('GET', `projekte/${encodeURIComponent(id)}`),
  seit: (id: string, nr: number) => anfrage<SeitAntwort>('GET', `projekte/${encodeURIComponent(id)}/seit/${nr}`),
  speichern: (id: string, aenderungen: Aenderung[]) =>
    anfrage<SpeicherAntwort>('POST', `projekte/${encodeURIComponent(id)}/aenderungen`, { aenderungen }),
  exportieren: (id: string) => anfrage<Daten>('GET', `projekte/${encodeURIComponent(id)}/export`),
  importieren: (kundeId: string, projektId: string, daten: Daten) =>
    anfrage<{ meldung: string }>('POST', `kunden/${encodeURIComponent(kundeId)}/projekte/${encodeURIComponent(projektId)}/import`, daten),

  // Kunden und Projekte (Superadmin)
  kundeAnlegen: (k: { name: string; id?: string; logo?: string | null; logoRatio?: number | null; intern?: boolean }) => anfrage<KundeDaten>('POST', 'kunden', k),
  kundeAendern: (id: string, k: { name?: string; logo?: string | null; logoRatio?: number | null; intern?: boolean }) => anfrage<KundeDaten>('PATCH', `kunden/${p(id)}`, k),
  kundeLoeschen: (id: string, bestaetigung: string) => anfrage<{ meldung: string }>('DELETE', `kunden/${p(id)}?bestaetigung=${p(bestaetigung)}`),
  kundeExportieren: (id: string) => anfrage<Daten>('GET', `kunden/${p(id)}/export`),
  /** Bei einer Kopie: wie viele Dateien mitkopiert wurden bzw. nicht übernommen werden konnten. */
  projektAnlegen: (np: NeuesProjekt) => anfrage<{ id: string; dateien?: number; dateienFehler?: number }>('POST', 'projekte', np),
  projektLoeschen: (id: string) => anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}`),
  projektArchivieren: (id: string) => anfrage<Archiv>('POST', `projekte/${p(id)}/archiv`),
  projektWiederherstellen: (id: string) => anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/archiv`),

  // Tägliche Sicherungen (Superadmin)
  sicherungen: (id: string) => anfrage<Sicherung[]>('GET', `projekte/${p(id)}/sicherungen`),
  sicherungZuruecksetzen: (id: string, am: string) =>
    anfrage<{ nr: number; meldung: string }>('POST', `projekte/${p(id)}/sicherungen/${p(am)}/zuruecksetzen`),

  // Personen des Projekts für Auswahlfelder (ab mitwirkend): nur Name und E-Mail
  personen: (id: string) => anfrage<{ personen: Person[] }>('GET', `projekte/${p(id)}/personen`),
  // Mitglieder (Admin)
  mitglieder: (id: string) => anfrage<{ mitglieder: Mitglied[]; vorschlaege: Person[] }>('GET', `projekte/${p(id)}/mitglieder`),
  // Kunde oder Projekt für mich als Favorit markieren (Stern auf der Startseite) – persönlich, auch in archivierten Projekten
  favoritSetzen: (art: 'kunde' | 'projekt', id: string, favorit: boolean) => anfrage<{ favorit: boolean }>('PUT', 'favoriten', { art, id, favorit }),
  mitgliedSetzen: (id: string, email: string, rolle: Rolle) => anfrage<{ mitglieder: Mitglied[]; einladung: Einladung; ansprechpartner?: TicketAnsprechpartnerStand | null }>('PUT', `projekte/${p(id)}/mitglieder`, { email, rolle }),
  mitgliedName: (id: string, email: string, name: string) => anfrage<{ mitglieder: Mitglied[] }>('PUT', `projekte/${p(id)}/mitglieder/name`, { email, name }),
  /** Firma einer E-Mail-Domäne benennen oder umbenennen (leer = entfernen); gilt für die Domäne in allen Projekten */
  /** Zuletzt gewählte Ansicht der angemeldeten Person in diesem Projekt (z. B. „aufgaben“) */
  ansichtLesen: (id: string, bereich: string) => anfrage<{ werte: Record<string, unknown> | null }>('GET', `projekte/${p(id)}/ansichten/${p(bereich)}`),
  ansichtSetzen: (id: string, bereich: string, werte: Record<string, unknown>) =>
    anfrage<{ werte: Record<string, unknown> }>('PUT', `projekte/${p(id)}/ansichten/${p(bereich)}`, { werte }),
  mitgliedFirma: (id: string, domaene: string, name: string) => anfrage<{ mitglieder: Mitglied[] }>('PUT', `projekte/${p(id)}/mitglieder/firma`, { domaene, name }),
  mitgliedEntfernen: (id: string, email: string) => anfrage<{ mitglieder: Mitglied[] }>('DELETE', `projekte/${p(id)}/mitglieder?email=${p(email)}`),

  // Zuständigkeiten übertragen (Admin): eingetragene Personen und Kürzel, Ersetzen durch ein Mitglied in allen Bereichen
  zustaendigkeiten: (id: string) => anfrage<{ personen: Zustaendigkeit[] }>('GET', `projekte/${p(id)}/zustaendigkeiten`),
  zustaendigkeitenUebertragen: (id: string, von: string, nach: string, nurOffene: boolean) =>
    anfrage<{ von: string; nach: string; anzahl: number; bereiche: Record<string, number> }>('POST', `projekte/${p(id)}/zustaendigkeiten`, { von, nach, nurOffene }),

  // Superadmins
  superadmins: () => anfrage<Superadmin[]>('GET', 'superadmins'),
  superadminErnennen: (email: string) => anfrage<{ superadmins: Superadmin[]; einladung: Einladung }>('PUT', 'superadmins', { email }),
  einladungSenden: (id: string, email: string) => anfrage<{ einladung: Einladung }>('POST', `projekte/${p(id)}/mitglieder/einladung?email=${p(email)}`),
  einladungenVorlage: (id: string) => anfrage<{ betreff: string; text: string; maxEmpfaenger: number }>('GET', `projekte/${p(id)}/mitglieder/einladungen`),
  einladungenSenden: (id: string, emails: string[], betreff: string, text: string) =>
    anfrage<{ ergebnisse: { email: string; einladung: Einladung }[] }>('POST', `projekte/${p(id)}/mitglieder/einladungen`, { emails, betreff, text }),
  superadminEntfernen: (email: string) => anfrage<Superadmin[]>('DELETE', `superadmins?email=${p(email)}`),

  // Ticketsystem checkITeasy (Superadmin): Stand der Einrichtung, Token hinterlegen/entfernen (kommt nie zurück), Kundennummern
  ticketsystem: () => anfrage<TicketsystemStand>('GET', 'ticketsystem'),
  ticketsystemToken: (token: string | null) => anfrage<TicketsystemStand>('PUT', 'ticketsystem', { token }),
  ticketsystemTest: () => anfrage<{ meldung: string }>('POST', 'ticketsystem/test'),
  ticketsystemKunden: () => anfrage<TicketKunde[]>('GET', 'ticketsystem/kunden'),
  kundeTicketsystem: (id: string, kto: string | null) => anfrage<TicketKundeZuordnung>('PUT', `kunden/${p(id)}/ticketsystem`, { kto }),

  // Ticketsystem je Projekt (Admin): Schalter, checkITeasy-Projekt, Queue, Kategorie; Auswahllisten kommen aus checkITeasy
  projektTicketsystem: (id: string) => anfrage<TicketEinstellungen>('GET', `projekte/${p(id)}/ticketsystem`),
  projektTicketsystemSetzen: (id: string, e: { aktiv?: boolean; ciProjektId?: number | null; queueId?: number | null; kategorieId?: number | null }) =>
    anfrage<TicketEinstellungen>('PUT', `projekte/${p(id)}/ticketsystem`, e),
  projektTicketsystemAuswahl: (id: string) => anfrage<TicketAuswahllisten>('GET', `projekte/${p(id)}/ticketsystem/auswahl`),

  // Benutzer (alle Personen, Superadmin)
  benutzer: () => anfrage<Benutzer[]>('GET', 'benutzer'),
  // Person mehreren Projekten zuordnen (rolle null = entfernen): Interne kundenübergreifend, Externe innerhalb eines Kunden
  benutzerProjekte: (email: string, projekte: { projektId: string; rolle: Rolle | null }[]) =>
    anfrage<{ aenderungen: { projektId: string; titel: string; kunde: string; rolle: Rolle | null; art: 'neu' | 'geaendert' | 'entfernt' }[]; einladung: Einladung; ansprechpartner?: TicketAnsprechpartnerStand | null }>('PUT', 'benutzer/projekte', { email, projekte }),
  benutzerEntfernen: (email: string) => anfrage<{ meldung: string }>('DELETE', `benutzer?email=${p(email)}`),
  // Aktivitätsprotokoll (Superadmin): Anmeldungen und Aktionen über alle Kunden und Projekte, Export als Excel
  aktivitaet: (f: AktivitaetFilter) => anfrage<AktivitaetAntwort>('GET', 'aktivitaet' + aktivitaetAbfrage(f)),
  aktivitaetExport: async (f: AktivitaetFilter): Promise<Blob> => {
    const res = await fetch('/api/aktivitaet/export' + aktivitaetAbfrage(f), { credentials: 'same-origin' });
    if (!res.ok) throw new ApiFehler(res.status, (await res.json().catch(() => null))?.meldung || `Fehler ${res.status}`);
    return res.blob();
  },

  // Excel-Export einer Liste (alle Mitglieder): Datei und Dateiname vom Server
  listeExport: async (id: string, bereich: ExportBereich): Promise<{ blob: Blob; name: string }> => {
    const res = await fetch(`/api/projekte/${p(id)}/export/${bereich}`, { credentials: 'same-origin' });
    if (!res.ok) throw new ApiFehler(res.status, (await res.json().catch(() => null))?.meldung || `Fehler ${res.status}`);
    return { blob: await res.blob(), name: dateinameAus(res.headers.get('Content-Disposition'), bereich + '.xlsx') };
  },

  // Kommentare
  kommentare: (id: string) => anfrage<Kommentar[]>('GET', `projekte/${p(id)}/kommentare`),
  kommentieren: (id: string, bezugTyp: BezugTyp, bezugId: string, text: string) =>
    anfrage<Kommentar>('POST', `projekte/${p(id)}/kommentare`, { bezugTyp, bezugId, text }),
  kommentarLoeschen: (id: string, kid: string) => anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/kommentare/${p(kid)}`),

  // Abschnitte (ab verantwortlich schreiben)
  abschnitte: (id: string) => anfrage<Abschnitt[]>('GET', `projekte/${p(id)}/abschnitte`),
  abschnittAnlegen: (id: string, name: string, reihenfolge?: number) =>
    anfrage<Abschnitt>('POST', `projekte/${p(id)}/abschnitte`, { name, reihenfolge }),
  /** 409 → ApiFehler.daten = { meldung, aktuell: Abschnitt } */
  abschnittAendern: (id: string, aid: string, aenderung: { name?: string; reihenfolge?: number; zeile?: string }) =>
    anfrage<Abschnitt>('PATCH', `projekte/${p(id)}/abschnitte/${p(aid)}`, aenderung),
  /** Aufgaben des Abschnitts wandern nach „nach“ (sonst ohne Abschnitt). */
  abschnittLoeschen: (id: string, aid: string, nach?: string) =>
    anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/abschnitte/${p(aid)}${nach ? `?nach=${p(nach)}` : ''}`),

  // Aufgaben (ab mitwirkend schreiben)
  aufgaben: (id: string, filter: AufgabenFilter = {}) => {
    const q = new URLSearchParams();
    if (filter.bezugTyp) q.set('bezugTyp', filter.bezugTyp);
    if (filter.bezugId) q.set('bezugId', filter.bezugId);
    if (filter.erledigt !== undefined) q.set('erledigt', String(filter.erledigt));
    const qs = q.toString();
    return anfrage<Aufgabe[]>('GET', `projekte/${p(id)}/aufgaben${qs ? '?' + qs : ''}`);
  },
  aufgabe: (id: string, aid: string) => anfrage<Aufgabe>('GET', `projekte/${p(id)}/aufgaben/${p(aid)}`),
  aufgabeAnlegen: (id: string, aufgabe: NeueAufgabe) => anfrage<Aufgabe>('POST', `projekte/${p(id)}/aufgaben`, aufgabe),
  /** Nur geänderte Felder schicken, dazu die gelesene zeile. 409 → ApiFehler.daten: AufgabeKonflikt. */
  aufgabeAendern: (id: string, aid: string, zeile: string, felder: AufgabeFelder) =>
    anfrage<AufgabeGeaendert>('PATCH', `projekte/${p(id)}/aufgaben/${p(aid)}`, { ...felder, zeile }),
  /** Löscht auch Unteraufgaben; mit zeile 409, falls inzwischen geändert. */
  aufgabeLoeschen: (id: string, aid: string, zeile?: string) =>
    anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/aufgaben/${p(aid)}${zeile ? `?zeile=${p(zeile)}` : ''}`),
  /** Aufgabe für mich merken bzw. nicht mehr merken (ab lesend; ändert die Aufgabe nicht). */
  aufgabeMerken: (id: string, aid: string, gemerkt: boolean) =>
    anfrage<{ gemerkt: boolean }>('PUT', `projekte/${p(id)}/aufgaben/${p(aid)}/gemerkt`, { gemerkt }),
  // Ticket in checkITeasy aus der Aufgabe erzeugen (Admin) → die Aufgabe mit ticket; Stand für die Ansicht ab lesend
  aufgabeTicketErzeugen: (id: string, aid: string) => anfrage<Aufgabe>('POST', `projekte/${p(id)}/aufgaben/${p(aid)}/ticket`),
  // Mit einem vorhandenen Ticket verknüpfen (Admin): offene Tickets des Kunden (q = Volltext über Nummer, Titel, Beschreibung), dann { ticketId }
  // Alle offenen Tickets des Kunden auf einmal; gefiltert wird im Dialog (jede Abfrage holt die Tickets neu aus checkITeasy)
  ticketAuswahl: (id: string) => anfrage<{ tickets: TicketWahl[] }>('GET', `projekte/${p(id)}/ticketsystem/tickets?alle=1`),
  aufgabeTicketVerknuepfen: (id: string, aid: string, ticketId: number) => anfrage<Aufgabe>('PUT', `projekte/${p(id)}/aufgaben/${p(aid)}/ticket`, { ticketId }),
  // Abgleich Ticket → Aufgabe: auto = beim Öffnen und alle fünf Minuten (ab lesend, läuft nur, wenn fällig); sonst sofort (Admin)
  ticketAbgleich: (id: string, auto: boolean) => anfrage<TicketAbgleich>('POST', `projekte/${p(id)}/ticketsystem/abgleich${auto ? '?auto=1' : ''}`),
  projektTicketStand: (id: string) => anfrage<TicketProjektStand>('GET', `projekte/${p(id)}/ticketsystem/stand`),
  aufgabeVerlauf: (id: string, aid: string) => anfrage<AufgabeVerlauf[]>('GET', `projekte/${p(id)}/aufgaben/${p(aid)}/verlauf`),
  /** Mir zugewiesene, mich beteiligende und von mir gemerkte Aufgaben über alle meine Projekte; erledigte nur auf Wunsch. */
  meineAufgaben: (erledigte = false) => anfrage<MeineAufgabe[]>('GET', `meine-aufgaben${erledigte ? '?erledigte=true' : ''}`),

  // Meilensteine und Phasen (ab verantwortlich schreiben)
  testfaelle: (id: string, f: { prozess?: string; schritt?: string } = {}) =>
    anfrage<Testfall[]>('GET', `projekte/${p(id)}/testfaelle${f.schritt ? '?schritt=' + p(f.schritt) : f.prozess ? '?prozess=' + p(f.prozess) : ''}`),
  testfallAnlegen: (id: string, t: TestfallFelder & { titel: string; schrittId?: string | null; prozessId?: string | null }) =>
    anfrage<Testfall>('POST', `projekte/${p(id)}/testfaelle`, t),
  testfallAendern: (id: string, tid: string, zeile: string, f: TestfallFelder) => anfrage<Testfall>('PATCH', `projekte/${p(id)}/testfaelle/${p(tid)}`, { ...f, zeile }),
  testfallLoeschen: (id: string, tid: string, zeile?: string) =>
    anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/testfaelle/${p(tid)}${zeile ? '?zeile=' + p(zeile) : ''}`),
  checklistenVorlagen: () => anfrage<ChecklistenVorlage[]>('GET', 'checklisten-vorlagen'),
  checklisten: (id: string) => anfrage<Checkliste[]>('GET', `projekte/${p(id)}/checklisten`),
  checklisteAnlegen: (id: string, c: { titel?: string; vorlage?: string | null }) => anfrage<Checkliste>('POST', `projekte/${p(id)}/checklisten`, c),
  checklisteAendern: (id: string, cid: string, zeile: string, f: { titel?: string; reihenfolge?: number }) =>
    anfrage<Checkliste>('PATCH', `projekte/${p(id)}/checklisten/${p(cid)}`, { ...f, zeile }),
  checklisteLoeschen: (id: string, cid: string) => anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/checklisten/${p(cid)}`),
  checklistenPunktAnlegen: (id: string, cid: string, f: ChecklistenPunktFelder & { text: string }) =>
    anfrage<ChecklistenPunkt>('POST', `projekte/${p(id)}/checklisten/${p(cid)}/punkte`, f),
  checklistenPunktAendern: (id: string, cid: string, pid: string, zeile: string, f: ChecklistenPunktFelder) =>
    anfrage<ChecklistenPunkt>('PATCH', `projekte/${p(id)}/checklisten/${p(cid)}/punkte/${p(pid)}`, { ...f, zeile }),
  checklistenPunktLoeschen: (id: string, cid: string, pid: string) =>
    anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/checklisten/${p(cid)}/punkte/${p(pid)}`),
  /** Kalender-Abo (ICS): Stand, neuen geheimen Link erzeugen (alter wird ungültig), beenden. Feed: /api/kalender/<token>.ics */
  kalenderAbo: () => anfrage<{ vorhanden: boolean; erstelltAm: string | null }>('GET', 'kalender-abo'),
  kalenderAboNeu: () => anfrage<{ token: string }>('POST', 'kalender-abo'),
  kalenderAboLoeschen: () => anfrage<{ meldung: string }>('DELETE', 'kalender-abo'),
  suche: (id: string, q: string) => anfrage<Suchtreffer[]>('GET', `projekte/${p(id)}/suche?q=${p(q)}`),
  /** Protokoll der Zugriffe und Änderungen (Admins): neueste zuerst, vor = ältere als diese Nummer. */
  protokoll: (id: string, vor?: number) => anfrage<import('./protokoll').Protokolleintrag[]>('GET', `projekte/${p(id)}/protokoll${vor ? '?vor=' + vor : ''}`),
  abnahmen: (id: string) => anfrage<Abnahme[]>('GET', `projekte/${p(id)}/abnahmen`),
  abnahmeFreigeben: (id: string, f: { prozessId?: string | null; bemerkung?: string | null }) => anfrage<Abnahme>('POST', `projekte/${p(id)}/abnahmen`, f),
  abnahmenVergleich: (id: string, links: string, rechts: string, prozess?: string | null) =>
    anfrage<Vergleich>('GET', `projekte/${p(id)}/abnahmen-vergleich?links=${p(links)}&rechts=${p(rechts)}${prozess ? '&prozess=' + p(prozess) : ''}`),
  staende: (id: string) => anfrage<Stand[]>('GET', `projekte/${p(id)}/staende`),
  stand: (id: string, nr: number) => anfrage<{ stand: Stand; daten: Daten }>('GET', `projekte/${p(id)}/staende/${nr}`),
  standFesthalten: (id: string, bezeichnung: string | null) => anfrage<Stand>('POST', `projekte/${p(id)}/staende`, { bezeichnung }),
  standBenennen: (id: string, nr: number, bezeichnung: string | null) => anfrage<Stand>('PATCH', `projekte/${p(id)}/staende/${nr}`, { bezeichnung }),
  abnahmeStand: (id: string, aid: string) => anfrage<{ abnahme: Abnahme; daten: Daten }>('GET', `projekte/${p(id)}/abnahmen/${p(aid)}`),
  meilensteine: (id: string) => anfrage<Meilenstein[]>('GET', `projekte/${p(id)}/meilensteine`),
  meilensteinAnlegen: (id: string, m: MeilensteinFelder & { titel: string }) => anfrage<Meilenstein>('POST', `projekte/${p(id)}/meilensteine`, m),
  /** Nur geänderte Felder schicken, dazu die gelesene zeile. 409 → ApiFehler.daten = { meldung, aktuell }. */
  meilensteinAendern: (id: string, mid: string, zeile: string, felder: MeilensteinFelder) =>
    anfrage<Meilenstein>('PATCH', `projekte/${p(id)}/meilensteine/${p(mid)}`, { ...felder, zeile }),
  meilensteinLoeschen: (id: string, mid: string, zeile?: string) =>
    anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/meilensteine/${p(mid)}${zeile ? `?zeile=${p(zeile)}` : ''}`),

  // Offene Punkte: Risiken, Annahmen, Klärungspunkte (ab mitwirkend schreiben)
  offenePunkte: (id: string) => anfrage<OffenerPunkt[]>('GET', `projekte/${p(id)}/offene-punkte`),
  offenerPunkt: (id: string, oid: string) => anfrage<OffenerPunkt>('GET', `projekte/${p(id)}/offene-punkte/${p(oid)}`),
  offenerPunktAnlegen: (id: string, op: PunktFelder & { titel: string }) => anfrage<OffenerPunkt>('POST', `projekte/${p(id)}/offene-punkte`, op),
  /** Nur geänderte Felder schicken, dazu die gelesene zeile. 409 → ApiFehler.daten = { meldung, aktuell }. */
  offenerPunktAendern: (id: string, oid: string, zeile: string, felder: PunktFelder) =>
    anfrage<OffenerPunkt>('PATCH', `projekte/${p(id)}/offene-punkte/${p(oid)}`, { ...felder, zeile }),
  /** Löscht auch die Kommentare des Punkts. */
  offenerPunktLoeschen: (id: string, oid: string, zeile?: string) =>
    anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/offene-punkte/${p(oid)}${zeile ? `?zeile=${p(zeile)}` : ''}`),

  // Projekt-Cockpit und Besprechungen
  cockpit: (id: string, heute?: string) => anfrage<CockpitDaten>('GET', `projekte/${p(id)}/cockpit${heute ? `?heute=${p(heute)}` : ''}`),
  besprechungen: (id: string) => anfrage<Besprechung[]>('GET', `projekte/${p(id)}/besprechungen`),
  /** Datei des Gesprächsprotokolls (.xlsx, .docx, .json) lesen lassen – schreibt nichts. */
  besprechungVorschau: (id: string, datei: File) => {
    const f = new FormData();
    f.append('datei', datei, datei.name);
    return anfrage<ProtokollVorschau>('POST', `projekte/${p(id)}/besprechungen/vorschau`, f);
  },
  besprechungImportieren: (id: string, b: BesprechungImport) => anfrage<Besprechung>('POST', `projekte/${p(id)}/besprechungen`, b),
  // Import aus Asana: das persönliche Token geht nur im Anfragekörper hinaus und bleibt im Speicher des Dialogs
  asanaPruefen: (token: string) => anfrage<AsanaPerson>('POST', 'asana/pruefen', { token }),
  asanaProjekte: (token: string, suche: string) => anfrage<AsanaProjekt[]>('POST', 'asana/projekte', { token, suche }),
  asanaLesen: (id: string, token: string, asanaProjekt: string, weiter: string | null) =>
    anfrage<AsanaPaket>('POST', `projekte/${p(id)}/asana/lesen`, { token, asanaProjekt, weiter }),
  asanaVorschau: (id: string, d: AsanaDaten) => anfrage<AsanaVorschau>('POST', `projekte/${p(id)}/asana/vorschau`, d),
  asanaImport: (id: string, d: AsanaDaten) => anfrage<AsanaErgebnis>('POST', `projekte/${p(id)}/asana/import`, d),
  asanaKommentare: (id: string, token: string, aufgaben: string[], personen: Record<string, AsanaPersonZiel>) =>
    anfrage<{ erledigt: string[]; kommentare: number }>('POST', `projekte/${p(id)}/asana/kommentare`, { token, aufgaben, personen }),
  asanaDateien: (id: string, token: string, aufgaben: string[]) =>
    anfrage<{ erledigt: string[]; dateien: number; links: number; fehlend: AsanaFehlend[] }>('POST', `projekte/${p(id)}/asana/dateien`, { token, aufgaben }),

  // Dokumente/Anhänge (ohne Filter: alle des Projekts, neueste zuerst)
  anhaenge: (id: string, bezugTyp?: AnhangBezugTyp, bezugId?: string) =>
    anfrage<Anhang[]>('GET', `projekte/${p(id)}/anhaenge${bezugTyp ? `?bezugTyp=${p(bezugTyp)}&bezugId=${p(bezugId ?? '')}` : ''}`),
  /** Upload durch die API (multipart, höchstens 25 MB). */
  anhangHochladen: (id: string, bezugTyp: AnhangBezugTyp, bezugId: string, datei: File) => {
    const f = new FormData();
    f.append('bezugTyp', bezugTyp);
    f.append('bezugId', bezugId);
    f.append('datei', datei, datei.name);
    return anfrage<Anhang>('POST', `projekte/${p(id)}/anhaenge`, f);
  },
  /** Link (SharePoint, Webseite …) an ein Element hängen; ohne Titel nimmt der Server den letzten Teil der Adresse */
  anhangLinkHinzufuegen: (id: string, bezugTyp: AnhangBezugTyp, bezugId: string, url: string, titel: string) =>
    anfrage<Anhang>('POST', `projekte/${p(id)}/anhaenge/links`, { bezugTyp, bezugId, url, titel }),
  anhangLink: (id: string, aid: string, anzeigen = false) =>
    anfrage<AnhangLink>('GET', `projekte/${p(id)}/anhaenge/${p(aid)}/link${anzeigen ? '?anzeigen=true' : ''}`),
  anhangLoeschen: (id: string, aid: string) => anfrage<{ meldung: string }>('DELETE', `projekte/${p(id)}/anhaenge/${p(aid)}`),
  /** Vorhandene Datei an weiterer Stelle verknüpfen (ab mitwirkend). */
  anhangVerknuepfen: (id: string, aid: string, bezugTyp: AnhangBezugTyp, bezugId: string) =>
    anfrage<Anhang>('POST', `projekte/${p(id)}/anhaenge/${p(aid)}/verknuepfungen`, { bezugTyp, bezugId }),
  /** Verknüpfung lösen – die Datei bleibt. */
  anhangLoesen: (id: string, aid: string, bezugTyp: AnhangBezugTyp, bezugId: string) =>
    anfrage<Anhang>('DELETE', `projekte/${p(id)}/anhaenge/${p(aid)}/verknuepfungen?bezugTyp=${p(bezugTyp)}&bezugId=${p(bezugId)}`),

  // Benachrichtigungen und eigene Einstellungen
  /** Eigene Benachrichtigungen (nur In-App), ungelesene zuerst; limit Standard 50, höchstens 200. */
  /** gelesen: true = nur gelesene, false = nur ungelesene */
  benachrichtigungen: (limit: number, gelesen: boolean) =>
    anfrage<BenachrichtigungenAntwort>('GET', `benachrichtigungen?limit=${limit}&gelesen=${gelesen}`),
  /** Bestimmte (ids) oder alle ('alle') als gelesen markieren → Zahl der übrigen ungelesenen. */
  benachrichtigungenGelesen: (ids: string[] | 'alle') =>
    anfrage<{ ungelesen: number }>('POST', 'benachrichtigungen/gelesen', ids === 'alle' ? { alle: true } : { ids }),
  einstellungen: () => anfrage<Einstellung[]>('GET', 'einstellungen'),
  /** Nur geänderte Arten schicken; fehlende Schalter bleiben. Antwort: alle Einstellungen. */
  einstellungenSpeichern: (aenderungen: EinstellungAenderung[]) => anfrage<Einstellung[]>('PUT', 'einstellungen', aenderungen),
};

// ---------- Benachrichtigungen ----------

export type BenachrichtigungArt = 'zugewiesen' | 'geaendert' | 'kommentar' | 'erwaehnt' | 'frist_bald' | 'ueberfaellig' | 'eingeladen';
export interface Benachrichtigung {
  id: string;
  projektId: string | null;
  projektTitel: string | null;
  art: BenachrichtigungArt;
  /** Titel der Aufgabe, des Schritts usw. bzw. des Projekts (eingeladen) */
  titel: string;
  text: string | null;
  /** Sprungziel: aufgabe, schritt, prozess, entscheidung oder projekt */
  bezugTyp: BezugTyp | null;
  bezugId: string | null;
  /** null bei Fristen (zeitgesteuerter Lauf) */
  ausloeser: string | null;
  ausloeserName: string | null;
  am: string;
  gelesenAm: string | null;
}
export interface BenachrichtigungenAntwort { ungelesen: number; eintraege: Benachrichtigung[] }
/** standard = nichts gespeichert, es gilt der Standard (In-App und E-Mail für alles). */
export interface Einstellung { art: BenachrichtigungArt; inApp: boolean; email: boolean; standard: boolean }
export interface EinstellungAenderung { art: BenachrichtigungArt; inApp?: boolean; email?: boolean }

// ---------- Import aus Asana ----------
export interface AsanaProjekt { gid: string; name: string; arbeitsbereich: string | null; link: string | null }
export interface AsanaPerson { gid?: string; name: string | null; email: string | null }
export interface AsanaAufgabe {
  gid: string; titel: string; abschnitt: string | null; eltern: string | null; art: string; zustaendig: AsanaPerson | null; follower: AsanaPerson[];
  faellig: string | null; erledigt: boolean; [feld: string]: unknown;
}
export interface AsanaPaket { projekt: AsanaProjekt | null; abschnitte: { gid: string; name: string }[]; aufgaben: AsanaAufgabe[]; weiter: string | null }
/** mitglied = vorhandenes Mitglied, hinzufuegen = als Mitglied ohne Einladung, name = nur als Name übernehmen */
export interface AsanaPersonZiel { art: 'mitglied' | 'hinzufuegen' | 'name'; email: string | null; rolle: Rolle | null }
export interface AsanaOptionen {
  erledigte: boolean; unteraufgaben: boolean; follower: boolean; abhaengigkeiten: boolean; meilensteine: boolean;
  prioFeld: string | null; prioWerte: Record<string, string>;
}
export interface AsanaDaten {
  projekt: AsanaProjekt | null; abschnitte: AsanaPaket['abschnitte']; aufgaben: AsanaAufgabe[];
  optionen?: AsanaOptionen; personen?: Record<string, AsanaPersonZiel>;
}
export interface AsanaVorschau {
  projekt: string | null; uebersprungen: number; hinweise: string[];
  zahlen: { aufgaben: number; unteraufgaben: number; meilensteine: number; erledigt: number; abhaengigkeiten: number; mitFaelligkeit: number };
  personen: { gid: string; name: string | null; email: string | null; verantwortlich: number; follower: number; mitglied: boolean; vorschlag: AsanaPersonZiel }[];
  felder: { name: string; werte: string[]; prioritaet: boolean; vorschlag: Record<string, string | null> }[];
  abschnitte: { name: string; vorhanden: boolean; aufgaben: number }[];
}
export interface AsanaErgebnis {
  importId: string; hinweise: string[]; aufgaben: string[];
  zahlen: { abschnitteNeu: number; abschnitteVorhanden: number; aufgaben: number; unteraufgaben: number; erledigt: number; meilensteine: number;
    abhaengigkeiten: number; beteiligte: number; mitgliederNeu: number; uebersprungen: number };
}
/** Datei aus Asana, die sich nicht übernehmen ließ – vor dem Abschalten von Asana von Hand sichern. */
export interface AsanaFehlend { aufgabe: string; datei: string; grund: string }
